/**
 * @module utils/gsap
 *
 * Shared GSAP + ScrollTrigger loader and refresh coordinator.
 *
 * GSAP and ScrollTrigger are loaded once and the registered instance is shared,
 * so every behaviour/global animates against the same plugin registration.
 *
 * `refreshScroll()` is the single entry point for "the layout changed, recompute
 * every trigger". Pins (TextCarousel), heading fits (FitHeading) and reveal
 * setup (FadeIn) all grow or shift the page at different, racing moments during
 * load; without a recompute, triggers created earlier keep stale start/end
 * positions and fire in the wrong place. Calls are batched into a single
 * post-frame refresh so a burst of layout changes costs one pass, not many.
 */

let loadPromise = null;

export function loadGsap() {
	if (!loadPromise) {
		loadPromise = Promise.all([
			import('gsap'),
			import('gsap/ScrollTrigger'),
		]).then(([{ default: gsap }, { ScrollTrigger }]) => {
			gsap.registerPlugin(ScrollTrigger);
			// Ignore the viewport resize the mobile address bar fires as it
			// shows/hides on scroll, so ScrollTrigger doesn't refresh and
			// recompute start/end positions mid-scroll. Pairs with the
			// svh-based heights, which stay fixed across that same resize.
			ScrollTrigger.config({ ignoreMobileResize: true });
			return { gsap, ScrollTrigger };
		});
	}
	return loadPromise;
}

let splitTextPromise = null;

export function loadSplitText() {
	if (!splitTextPromise) {
		splitTextPromise = Promise.all([
			loadGsap(),
			import('gsap/SplitText'),
			import('gsap/CustomEase'),
		]).then(([{ gsap, ScrollTrigger }, { SplitText }, { CustomEase }]) => {
			gsap.registerPlugin(SplitText, CustomEase);
			// Mirror the `--ease-this` CSS token: cubic-bezier(0.4, 0, 0.2, 1).
			// Registered by name so tweens can use `ease: 'ease-this'`.
			CustomEase.create('ease-this', 'M0,0 C0.4,0 0.2,1 1,1');
			return { gsap, ScrollTrigger, SplitText };
		});
	}
	return splitTextPromise;
}

let refreshQueued = false;

export function refreshScroll() {
	if (refreshQueued) return;
	refreshQueued = true;
	requestAnimationFrame(() => {
		refreshQueued = false;
		loadGsap().then(({ ScrollTrigger }) => ScrollTrigger.refresh());
	});
}
